// ads-templates/common/AdsTemplateTable.tsx
import { DataGridPro, GridFilterModel } from "@mui/x-data-grid-pro";
import { Box, Chip } from "@material-ui/core";
import { AdsPlatformAdapter } from "../adapters/types";
import { useState, useEffect } from "react";
import { useUser } from "../../../../Auth";
import NotificationPopup from "../../../../common/NotificationPopup";
import CreateView from "../../../custom/CreateView";
import ConfirmButton from "../../../../components/ConfirmButton";
import { DataGridLocale } from "../../../../common/DataGridLocale";
import { InputType } from "../../../../common/FormRenderer";
import AdsCreateTemplate from "./AdsCreateTemplateView";
import TransferAssigneeDialog from "./TransferAssigneeDialog";

export default function AdsTemplateTable({ adapter, EditDialog }: {
  adapter: AdsPlatformAdapter;
  EditDialog?:  React.ComponentType<{ template: any }>;
}) {
  const user = useUser();
  const { data, loading, error } = adapter.useGetTemplatesQuery();
  const [removeTemplate] = adapter.useRemoveTemplateMutation();
  const [createRecord] = adapter.useCreateRecordMutation();
  const [filterModel, setFilterModel] = useState<
        GridFilterModel | undefined
      >();

  useEffect(() => {
    setFilterModel({ items: [{ columnField: "assignee", value: user?.name, operatorValue: "equals" }] });
  }, [user]);

  const rows = data?.templates ?? [];

  const onRemove = async (id: any) => {
    try {
      await removeTemplate({
        variables: { id },
        refetchQueries: [adapter.refetchTemplatesQueryName],
      });
      NotificationPopup.success("刪除完成");
    } catch (e:any) {
      NotificationPopup.error(`刪除發生問題: ${e.message}`);
    }
  };

  const columns = [
    { field: "id", headerName: "id", width: 100, type: "number", hide: true },
    { field: "name", headerName: `${adapter.displayName} 報表名稱`, width: 220 },
    {
      field: "adTypes",
      headerName: "廣告類型/欄位",
      width: 520,
      renderCell: (params:any) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* adTypes（如果有） */}
          {Array.isArray(params.row.adTypes) && params.row.adTypes.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {params.row.adTypes.map((t:string) => (
                <Chip 
                  size="small" 
                  key={t} 
                  label={adapter.formatField(t)}
                  style={{ backgroundColor: '#A5D6A7', color: 'white' }}
                />
              ))}
            </div>
          )}
          {/* reportNames */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {params.row.reportNames?.map((n:string) => (
              <Chip 
                size="small" 
                key={n} 
                label={n.split('-').map(adapter.formatField).join('-')} 
                style={{ backgroundColor: '#81D4FA', color: 'white' }}
              />
            ))}
          </div>
          {/* metricsFields */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {params.row.metricsFields?.map((f:string) => (
              <Chip 
                size="small" 
                key={f} 
                label={adapter.formatField(f)} 
                style={{ backgroundColor: '#FFAB91', color: 'white' }}
              />
            ))}
          </div>
        </div>
      )
    },
    { field: "assignee", headerName: "負責人", valueGetter: (p:any)=>p.row.assignee?.name },
    {
      type: "actions",
      field: "gen",
      headerName: "",
      width: 90,
      getActions: (params:any) => [
        <CreateView
          key={`gen-${params.row.id}`}
          submitTitle="儲存"
          title="產生報表"
          columns={[
            { name: "startDate", label: "開始日期", type: InputType.date },
            { name: "endDate", label: "結束日期", type: InputType.date },
          ]}
          onSubmit={async (res:any, _helpers:any, setOpen:(b:boolean)=>void) => {
            await createRecord({
              variables: {
                [adapter.recordInputKey]: {
                  templateId: parseInt(params.row.id),
                  startDate: res.startDate,
                  endDate: res.endDate,
                  name: params.row.name,
                  assigneeId: user?.id,
                }
              },
              refetchQueries: [adapter.refetchRecordListQueryName],
            });
            setOpen(false);
            NotificationPopup.success(`正在產生報表，稍後請至"報表紀錄"下載報表。`);
          }}
          queryEntity={adapter.generateQueryEntity}
        />
      ]
    },
    {
      type: "actions",
      field: "edit",
      headerName: "",
      width: 90,
      getActions: (p:any) => EditDialog ? [ <EditDialog key={`edit-${p.row.id}`} template={p.row}/> ] : []
    },
    {
      type: "actions",
      field: "copy",
      headerName: "",
      width: 90,
      disableColumnMenu: true,
      disableColumnSelector: false,
      sortable: false,
      getActions: (params:any) => [
        <AdsCreateTemplate 
          key={`copy-${params.row.id}`}
          adapter={adapter} 
          template={params.row} 
          isCopy={true}
        />
      ]
    },
    {
      type: "actions",
      field: "transfer",
      headerName: "",
      width: 105,
      disableColumnMenu: true,
      disableColumnSelector: false,
      sortable: false,
      getActions: (params:any) => [
        <TransferAssigneeDialog 
          key={`transfer-${params.row.id}`} 
          template={params.row} 
          adapter={adapter}
        />
      ]
    },
    {
      type: "actions",
      field: "del",
      headerName: "",
      width: 90,
      getActions: (p:any) => [
        <ConfirmButton
          key={`del-${p.row.id}`}
          onConfirm={async () => { await onRemove(p.row.id); }}
          queryEntity="template"
          queryAction="刪除廣告模板"
          entityId={p.row.id}
          queryMessage={`名稱: ${p.row.name}`}
        >
          刪除
        </ConfirmButton>
      ]
    },
  ];

  console.log(filterModel)
  return (
    <Box width="100%" style={{ marginTop: 30, height: "80vh" }}>
      <DataGridPro
        rows={rows}
        columns={columns as any}
        localeText={DataGridLocale}
        hideFooter
        autoHeight
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        rowHeight={150}
      />
    </Box>
  );
}
